/**
 * Upgrade navigation helpers — used everywhere in the app that needs to
 * push a merchant toward an upgrade. Standardises the URL shape so we
 * don't have modals + ad-hoc `window.location.href = '/settings/billing'`
 * calls scattered across features.
 *
 * Route-based upgrade flow:
 *   1. Feature X wants a higher tier
 *   2. call navigateToUpgrade(navigate, { tier: 'PRO' }) — routes straight
 *      to /upgrade/checkout so the merchant gets the summary + pay button
 *      for the exact tier that unlocks their feature.
 *   3. If we don't know which tier, call navigateToUpgrade(navigate) —
 *      routes to /upgrade so they can pick a plan.
 *
 * No backend / feature-access logic here — only URL construction.
 */

/**
 * @typedef {Object} UpgradeTarget
 * @property {string} [tier]    Enum key (FREE / PREMIUM / PRO / PRO_PLUS or admin-added)
 * @property {string} [name]    Display name for the tier — falls back to `tier`
 * @property {'upgrade'|'downgrade'} [action]  Defaults to 'upgrade'
 */

/**
 * Build the checkout URL for a specific tier target, or /upgrade if the
 * caller doesn't know which tier the merchant needs.
 */
export function buildUpgradePath(target) {
  if (!target || !target.tier) return '/upgrade';
  const params = new URLSearchParams({
    tier: target.tier,
    action: target.action || 'upgrade',
    name: target.name || target.tier,
  });
  return `/upgrade/checkout?${params.toString()}`;
}

/**
 * Fire-and-forget navigation. Pass a react-router `navigate` fn plus an
 * optional tier target. Handles the "no navigate hook available" case
 * (some legacy files use window.location) by falling back to href.
 */
export function navigateToUpgrade(navigate, target) {
  const path = buildUpgradePath(target);
  if (typeof navigate === 'function') {
    navigate(path);
  } else if (typeof window !== 'undefined') {
    window.location.href = path;
  }
}
